/**
 * Minimal CodeMirror 6 ↔ LoroText binding (spec §7.4.3/§7.4.4, decision S3).
 *
 * loro-codemirror 0.3.3's `LoroExtensions` works for nested texts, but its undo plugin takes over
 * the shared UndoManager's `setOnPush`/`setOnPop` (last binding wins) and its sync plugin ignores
 * local-origin events, so page-level undo would not be mirrored into the editor. This binding:
 * - writes local CodeMirror changes to the LoroText and commits them with an origin of its own;
 * - applies every Loro event not carrying that origin (imports, undo/redo, checkout, and local commits
 *   of *another* view of the same text — a block and its mirror in one tab, spec §8.18, spike S13) as
 *   CodeMirror changes, annotated so they are not written back.
 * Cursor presence for code blocks is `[Later]`.
 */
import { Annotation, type ChangeSpec, type Extension } from "@codemirror/state";
import { EditorView, ViewPlugin, type PluginValue, type ViewUpdate } from "@codemirror/view";
import type { LoroDoc, LoroEventBatch, LoroText } from "loro-crdt";

export const fromLoro = Annotation.define<boolean>();

let nextBinding = 1;

export function loroTextBinding(doc: LoroDoc, text: LoroText): Extension {
  return ViewPlugin.define(
    (view) =>
      new (class implements PluginValue {
        private unsub: () => void;
        private applying = false;
        private readonly origin = `cm:${nextBinding++}`;
        constructor() {
          // initial content
          const current = text.toString();
          if (view.state.doc.toString() !== current) {
            this.applying = true;
            view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: current }, annotations: fromLoro.of(true) });
            this.applying = false;
          }
          this.unsub = doc.subscribe((batch: LoroEventBatch) => this.onEvent(batch));
        }
        onEvent(batch: LoroEventBatch) {
          if (batch.origin === this.origin) return;
          const changes: ChangeSpec[] = [];
          for (const ev of batch.events) {
            if (ev.target !== text.id || ev.diff.type !== "text") continue;
            let pos = 0;
            for (const d of ev.diff.diff) {
              if (d.insert) {
                changes.push({ from: pos, to: pos, insert: d.insert });
              } else if (d.delete) {
                changes.push({ from: pos, to: pos + d.delete });
                pos += d.delete;
              } else if (d.retain != null) {
                pos += d.retain;
              }
            }
          }
          if (!changes.length) return;
          this.applying = true;
          try {
            view.dispatch({ changes, annotations: fromLoro.of(true) });
          } catch (e) {
            // out-of-sync (should not happen); resync from the source of truth
            console.warn("loroTextBinding resync", e);
            view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: text.toString() }, annotations: fromLoro.of(true) });
          } finally {
            this.applying = false;
          }
        }
        update(u: ViewUpdate) {
          if (!u.docChanged || this.applying) return;
          if (u.transactions.some((tr) => tr.annotation(fromLoro))) return;
          let adj = 0;
          u.changes.iterChanges((fromA, toA, _fromB, _toB, inserted) => {
            const ins = inserted.sliceString(0, inserted.length, "\n");
            if (toA > fromA) text.delete(fromA + adj, toA - fromA);
            if (ins.length) text.insert(fromA + adj, ins);
            adj += ins.length - (toA - fromA);
          });
          doc.commit({ origin: this.origin });
        }
        destroy() {
          this.unsub();
        }
      })(),
  );
}

export { EditorView };
